import {
  BoxGeometry,
  Box3,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  SphereGeometry,
  type BufferGeometry,
  type Material,
  type Object3D,
} from 'three';

/**
 * JEFE-3 El Padrinito (GDD §9.2, §4.3, §3.4, issue #122): a parody caricature, never a real name or insignia. A phone-obsessed
 * bureaucrat of crime in a brown suit with a thin moustache, seated at his desk, which floats under a hot-air balloon with
 * the rotary phone ringing. Low-poly with flat shading, from primitives, about 3.2 x 3.2 u (the body hitbox is 75 % of it).
 * The retreat deflates the balloon while he keeps arguing on the phone: nobody dies (GDD §3.4).
 */
export interface Jefe3Model {
  root: Group;
  desk: Group;
  balloon: Group;
  phone: Group;
  /** The handset, which vibrates when the phone rings. */
  handset: Mesh;
  capo: Group;
  /** The arm that holds the phone to the ear, dials or waves while he argues. */
  arm: Group;
  dispose(): void;
}

export const JEFE_3_COLORS = {
  balloon: 0xd9669a,
  balloonBelt: 0xf2c230,
  rope: 0xc9b896,
  desk: 0x6b4a32,
  deskTop: 0x8a6244,
  paper: 0xefe9d8,
  suit: 0x7a4a2a,
  shirt: 0xe9e2c8,
  skin: 0xd9a37a,
  moustache: 0x2a2018,
  phone: 0x2b2b2b,
  dial: 0xc9c4b4,
  cable: 0x4a4f55,
} as const;

export function buildJefe3(): Jefe3Model {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const part = <G extends BufferGeometry>(geometry: G, color: number, at: [number, number, number], parent?: Object3D) => {
    geometries.push(geometry);
    const mesh = new Mesh(geometry, paint(color));
    mesh.position.set(...at);
    parent?.add(mesh);
    return mesh;
  };

  const root = new Group();
  root.name = 'boss:JEFE-3';
  root.userData.pose = 'ON_THE_PHONE';

  // The balloon: a faceted pink envelope with a yellow belt, tied to the desk with four ropes. It deflates in the retreat.
  const balloon = new Group();
  balloon.name = 'boss:JEFE-3:balloon';
  balloon.position.set(0, 0.9, 0);
  part(new IcosahedronGeometry(1.1, 1), JEFE_3_COLORS.balloon, [0, 0, 0], balloon);
  part(new CylinderGeometry(1.08, 1.08, 0.2, 10), JEFE_3_COLORS.balloonBelt, [0, 0, 0], balloon);
  root.add(balloon);
  for (const side of [-1, 1]) {
    for (const z of [-0.35, 0.35]) {
      const rope = part(new BoxGeometry(0.04, 1.35, 0.04), JEFE_3_COLORS.rope, [side * 0.85, -0.1, z], root);
      rope.rotation.z = side * -0.5;
    }
  }

  // The desk: a wide top with two legs and a few papers.
  const desk = new Group();
  desk.name = 'boss:JEFE-3:desk';
  part(new BoxGeometry(3.2, 0.2, 1.2), JEFE_3_COLORS.deskTop, [0, -0.8, 0], desk);
  part(new BoxGeometry(0.2, 0.3, 1.0), JEFE_3_COLORS.desk, [-1.4, -1.05, 0], desk);
  part(new BoxGeometry(0.2, 0.3, 1.0), JEFE_3_COLORS.desk, [1.4, -1.05, 0], desk);
  part(new BoxGeometry(0.5, 0.04, 0.4), JEFE_3_COLORS.paper, [-0.9, -0.68, 0.2], desk);
  part(new BoxGeometry(0.45, 0.04, 0.35), JEFE_3_COLORS.paper, [-0.5, -0.64, -0.1], desk);
  root.add(desk);

  // The rotary phone on the desk: a base, a dial and a handset joined by a cable.
  const phone = new Group();
  phone.name = 'boss:JEFE-3:phone';
  phone.position.set(0.9, -0.58, 0.6);
  part(new BoxGeometry(0.5, 0.18, 0.4), JEFE_3_COLORS.phone, [0, 0.04, 0], phone);
  part(new CylinderGeometry(0.14, 0.14, 0.04, 10), JEFE_3_COLORS.dial, [0, 0.15, 0], phone);
  const handset = part(new BoxGeometry(0.55, 0.1, 0.12), JEFE_3_COLORS.phone, [0, 0.24, 0], phone);
  handset.name = 'boss:JEFE-3:handset';
  part(new BoxGeometry(0.04, 0.3, 0.04), JEFE_3_COLORS.cable, [0.3, 0.05, 0.1], phone);
  root.add(phone);

  // The capo seated behind the desk: brown suit, white shirt, thin moustache, and the arm with the phone.
  const capo = new Group();
  capo.name = 'boss:JEFE-3:capo';
  capo.position.set(-0.4, -0.3, 0.7);
  part(new BoxGeometry(0.55, 0.65, 0.35), JEFE_3_COLORS.suit, [0, 0, 0], capo);
  part(new BoxGeometry(0.14, 0.45, 0.37), JEFE_3_COLORS.shirt, [0, 0.05, 0], capo);
  part(new SphereGeometry(0.22, 6, 4), JEFE_3_COLORS.skin, [0, 0.55, 0], capo);
  part(new BoxGeometry(0.3, 0.04, 0.06), JEFE_3_COLORS.moustache, [0, 0.5, 0.2], capo);
  const arm = new Group();
  arm.name = 'boss:JEFE-3:arm';
  arm.position.set(0.3, 0.25, 0.1);
  part(new BoxGeometry(0.14, 0.5, 0.14), JEFE_3_COLORS.suit, [0, -0.2, 0], arm);
  capo.add(arm);
  root.add(capo);

  root.visible = false;
  return {
    root,
    desk,
    balloon,
    phone,
    handset,
    capo,
    arm,
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}

/** The visual size of the model (u) with the balloon, the desk and the ropes, for the hitbox of the body (70 to 80 %). */
export function visualSize(model: Jefe3Model): { width: number; height: number } {
  const box = new Box3().setFromObject(model.root);
  return { width: box.max.x - box.min.x, height: box.max.y - box.min.y };
}
